/**
 * Dev screen: Push notification testing
 */

import { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, FlatList } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { ArrowLeft, Send, Bell, RefreshCw } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { deliverPendingNotifications } from '@/lib/pushDelivery';

interface NotificationRow {
  id: string;
  type: string;
  title: string;
  body: string;
  status: string;
  created_at: string;
  sent_at: string | null;
}

export default function NotificationTestScreen() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const router = useRouter();

  const [notifications, setNotifications] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [deliveryResult, setDeliveryResult] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    const { data, error } = await supabase
      .from('notifications')
      .select('id, type, title, body, status, created_at, sent_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20);

    if (!error && data) {
      setNotifications(data);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // V1: Lock dev routes in production
  useEffect(() => {
    if (!__DEV__) { router.replace('/'); }
  }, [router]);

  if (!__DEV__) return null;

  const handleInsertTest = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { error } = await supabase.from('notifications').insert({
        user_id: user.id,
        type: 'test',
        title: 'Test Notification',
        body: `Test at ${new Date().toLocaleTimeString()}`,
        data: { test: true },
        status: 'pending',
      });
      if (error) {
        Alert.alert('Oops', "Couldn't complete the test. Please try again.");
      } else {
        await fetchNotifications();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeliver = async () => {
    setLoading(true);
    setDeliveryResult(null);
    try {
      const result = await deliverPendingNotifications(supabase);
      setDeliveryResult(`Sent: ${result.sent}, Failed: ${result.failed}`);
      await fetchNotifications();
    } catch (e: any) {
      setDeliveryResult(`Error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()}>
          <ArrowLeft size={24} color={theme.colors.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: theme.colors.text }]}>
          Notification Test
        </Text>
        <TouchableOpacity onPress={fetchNotifications}>
          <RefreshCw size={20} color={theme.colors.textSecondary} />
        </TouchableOpacity>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.actions}>
          <TouchableOpacity
            style={[styles.actionBtn, { backgroundColor: theme.colors.accent }]}
            onPress={handleInsertTest}
            disabled={loading}>
            <Bell size={18} color={theme.colors.accentForeground} />
            <Text style={[styles.actionBtnText, { color: theme.colors.accentForeground }]}>Insert Test Notification</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionBtn, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.accent, borderWidth: 1 }]}
            onPress={handleDeliver}
            disabled={loading}>
            <Send size={18} color={theme.colors.accent} />
            <Text style={[styles.actionBtnText, { color: theme.colors.accent }]}>
              Deliver Pending
            </Text>
          </TouchableOpacity>
        </View>

        {deliveryResult && (
          <View style={[styles.resultBox, { backgroundColor: theme.colors.surfaceVariant }]}>
            <Text style={[styles.resultText, { color: theme.colors.text }]}>{deliveryResult}</Text>
          </View>
        )}

        <Text style={[styles.sectionTitle, { color: theme.colors.textSecondary }]}>
          Recent Notifications ({notifications.length})
        </Text>

        {notifications.map((n) => (
          <View
            key={n.id}
            style={[styles.notifCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
            <View style={styles.notifHeader}>
              <Text style={[styles.notifType, { color: theme.colors.accent }]}>{n.type}</Text>
              <Text style={[styles.notifStatus, {
                color: n.status === 'sent' ? '#22c55e' : n.status === 'failed' ? '#ef4444' : theme.colors.textTertiary,
              }]}>
                {n.status}
              </Text>
            </View>
            <Text style={[styles.notifTitle, { color: theme.colors.text }]}>{n.title}</Text>
            <Text style={[styles.notifBody, { color: theme.colors.textSecondary }]}>{n.body}</Text>
            <Text style={[styles.notifDate, { color: theme.colors.textTertiary }]}>
              {new Date(n.created_at).toLocaleString()}
            </Text>
          </View>
        ))}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerTitle: { fontSize: 18, fontWeight: '700' },
  content: { padding: 16, gap: 16 },
  actions: { gap: 10 },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 2,
  },
  actionBtnText: { fontSize: 15, fontWeight: '700' },
  resultBox: { padding: 12, borderRadius: 2 },
  resultText: { fontSize: 14, fontWeight: '600', textAlign: 'center' },
  sectionTitle: { fontSize: 13, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5 },
  notifCard: { borderWidth: 1, borderRadius: 2, padding: 12, gap: 4 },
  notifHeader: { flexDirection: 'row', justifyContent: 'space-between' },
  notifType: { fontSize: 12, fontWeight: '700', textTransform: 'uppercase' },
  notifStatus: { fontSize: 12, fontWeight: '700' },
  notifTitle: { fontSize: 14, fontWeight: '700' },
  notifBody: { fontSize: 13 },
  notifDate: { fontSize: 11, marginTop: 4 },
});
